import { createTheme } from '@mui/material';

import {
    HF_BORDER_WIDTHS,
    HF_BORDERS,
    HF_BRAND_COLORS,
    HF_CHART_COLORS,
    HF_CTA_COLORS,
    HF_FONT_FAMILIES,
    HF_FONT_SIZES,
    HF_FONT_WEIGHTS,
    HF_ICON_SIZES,
    HF_LINE_HEIGHTS,
    HF_MOOD_COLORS,
    HF_NEUTRAL_COLORS,
    HF_RADIUS,
    HF_SEMANTIC_COLORS,
    HF_SHADOWS,
    HF_SPACING,
    HF_TEXT_COLORS,
    HF_Z_INDEX
} from './designTokens';
import { HF_LAYOUT_COLORS, HF_LAYOUT_OPACITY, HF_LAYOUT_SIZES } from './layoutConstants';
import { hfCssBaselineStyleOverrides } from './hfCssBaselineOverrides';
import ExtendedTypographyOptions from './theme-extensions';
import type {} from '@mui/x-tree-view/themeAugmentation';

/** MUI components use plain Helvetica (matches production dashboard). Body uses HF_BODY_STYLES via CssBaseline. */
const defaultFontFamily = HF_FONT_FAMILIES.uiPlain;
const defaultTextColor = HF_TEXT_COLORS.body;

const px = (n: number) => `${n}px`;

export const hfMuiTheme = createTheme({
    // shadows: Array(25).fill('none') as Shadows, // Remove shadows on the 25 levels required by MUI
    palette: {
        primary: {
            main: HF_CTA_COLORS[500],
            dark: '#185195',  // legacy production dark — preserved for existing consumers
            contrastText: HF_BRAND_COLORS.white
        },
        secondary: {
            main: HF_BRAND_COLORS.white,
            dark: HF_BRAND_COLORS.white,
            contrastText: HF_CTA_COLORS[500]
        },
        success: {
            main: HF_SEMANTIC_COLORS.success[500],
            dark: HF_SEMANTIC_COLORS.success[600],
            light: HF_SEMANTIC_COLORS.success[100],
            contrastText: HF_BRAND_COLORS.white
        },
        warning: {
            main: HF_SEMANTIC_COLORS.warning[500],
            dark: HF_SEMANTIC_COLORS.warning[600],
            light: HF_SEMANTIC_COLORS.warning[100],
            contrastText: HF_BRAND_COLORS.white
        },
        error: {
            main: HF_SEMANTIC_COLORS.error[500],
            dark: HF_SEMANTIC_COLORS.error[700],
            light: HF_SEMANTIC_COLORS.error[200],
            contrastText: HF_BRAND_COLORS.white
        },
        text: {
            primary: defaultTextColor,
            secondary: HF_TEXT_COLORS.secondary,
            disabled: HF_TEXT_COLORS.disabled
        },
        divider: HF_NEUTRAL_COLORS[200],
        background: {
            default: HF_NEUTRAL_COLORS['000'],
            paper: HF_BRAND_COLORS.white
        },
        hfLayout: { ...HF_LAYOUT_COLORS },
        hfBrand: { ...HF_BRAND_COLORS },
        hfMood: { ...HF_MOOD_COLORS },
        hfCta: { ...HF_CTA_COLORS },
        hfNeutral: { ...HF_NEUTRAL_COLORS },
        hfSemantic: { ...HF_SEMANTIC_COLORS },
        hfChart: { ...HF_CHART_COLORS }
    },
    hfLayout: {
        sizes: { ...HF_LAYOUT_SIZES },
        opacity: { ...HF_LAYOUT_OPACITY }
    },
    hf: {
        spacing: { ...HF_SPACING },
        radius: { ...HF_RADIUS },
        shadows: { ...HF_SHADOWS },
        fonts: { ...HF_FONT_FAMILIES }
    },
    iconSizes: {
        small: 16,
        medium: 24,
        large: 32,
        xlarge: 48
    },
    shape: {
        borderRadius: HF_RADIUS.md
    },
    typography: {
        fontFamily: defaultFontFamily,
        spacing: 4,
        fontSize: HF_FONT_SIZES.body,
        htmlFontSize: HF_FONT_SIZES.body,

        h1: {
            fontFamily: defaultFontFamily,
            fontSize: px(HF_FONT_SIZES.h1),
            fontStyle: 'normal',
            fontWeight: HF_FONT_WEIGHTS.bold,
            lineHeight: px(HF_LINE_HEIGHTS.h1),
            letterSpacing: '0em',
            color: defaultTextColor
        },
        h2: {
            fontFamily: defaultFontFamily,
            fontSize: px(HF_FONT_SIZES.h2),
            fontStyle: 'normal',
            fontWeight: HF_FONT_WEIGHTS.bold,
            lineHeight: px(HF_LINE_HEIGHTS.h2),
            letterSpacing: '0em'
        },
        h3: {
            fontFamily: defaultFontFamily,
            fontSize: px(HF_FONT_SIZES.h3),
            fontStyle: 'normal',
            fontWeight: HF_FONT_WEIGHTS.bold,
            lineHeight: px(HF_LINE_HEIGHTS.h3),
            letterSpacing: '0em'
        },
        h4: {
            fontFamily: defaultFontFamily,
            fontSize: px(HF_FONT_SIZES.h3),
            fontStyle: 'normal',
            fontWeight: HF_FONT_WEIGHTS.bold,
            lineHeight: px(HF_LINE_HEIGHTS.body),
            letterSpacing: '0em'
        },
        h5: {
            fontFamily: defaultFontFamily,
            fontSize: px(HF_FONT_SIZES.small),
            fontStyle: 'normal',
            fontWeight: HF_FONT_WEIGHTS.bold,
            lineHeight: px(HF_LINE_HEIGHTS.small),
            letterSpacing: '0em'
        },
        h6: {
            fontFamily: defaultFontFamily,
            fontSize: px(HF_FONT_SIZES.small),
            fontStyle: 'normal',
            fontWeight: HF_FONT_WEIGHTS.bold,
            lineHeight: px(HF_LINE_HEIGHTS.small),
            letterSpacing: '0em'
        },
        subtitle1: {
            fontFamily: defaultFontFamily,
            fontSize: px(HF_FONT_SIZES.small),
            fontStyle: 'normal',
            fontWeight: HF_FONT_WEIGHTS.regular,
            lineHeight: px(HF_LINE_HEIGHTS.small),
            letterSpacing: '0em'
        },
        subtitle2: {
            fontFamily: defaultFontFamily,
            fontSize: px(HF_FONT_SIZES.small),
            fontStyle: 'normal',
            fontWeight: HF_FONT_WEIGHTS.regular,
            lineHeight: px(HF_LINE_HEIGHTS.small),
            letterSpacing: '0em'
        },
        body1: {
            fontFamily: defaultFontFamily,
            fontSize: px(HF_FONT_SIZES.h3),
            fontStyle: 'normal',
            fontWeight: HF_FONT_WEIGHTS.regular,
            lineHeight: px(HF_LINE_HEIGHTS.h3),
            letterSpacing: '0em'
        },
        body2: {
            fontFamily: defaultFontFamily,
            fontSize: px(HF_FONT_SIZES.body),
            fontStyle: 'normal',
            fontWeight: HF_FONT_WEIGHTS.regular,
            lineHeight: px(HF_LINE_HEIGHTS.body),
            letterSpacing: '0em'
        },
        body3: {
            fontFamily: defaultFontFamily,
            fontSize: px(HF_FONT_SIZES.small),
            fontStyle: 'normal',
            fontWeight: HF_FONT_WEIGHTS.regular,
            lineHeight: px(HF_LINE_HEIGHTS.small),
            letterSpacing: '0em'
        },
        caption: {
            fontFamily: defaultFontFamily,
            fontSize: px(HF_FONT_SIZES.mini),
            fontStyle: 'normal',
            fontWeight: HF_FONT_WEIGHTS.regular,
            lineHeight: px(HF_LINE_HEIGHTS.mini),
            letterSpacing: '0em'
        },
        button: {
            fontSize: px(HF_FONT_SIZES.small),
            fontWeight: HF_FONT_WEIGHTS.regular,
            lineHeight: px(HF_LINE_HEIGHTS.bodyTight)
        },

        bigNumber: {
            fontSize: px(HF_FONT_SIZES.bigNumber),
            fontStyle: 'normal',
            fontWeight: HF_FONT_WEIGHTS.bold,
            lineHeight: px(HF_LINE_HEIGHTS.bigNumber)
        },

        /** Brand display style — Source Sans Pro for hero / cover / login surfaces. */
        hfDisplay: {
            fontFamily: HF_FONT_FAMILIES.brand,
            fontSize: px(HF_FONT_SIZES.bigNumber),
            fontStyle: 'normal',
            fontWeight: HF_FONT_WEIGHTS.bold,
            lineHeight: 1.1,
            letterSpacing: '-.5px',
            color: HF_BRAND_COLORS.white
        }
    } as ExtendedTypographyOptions,
    components: {

        MuiCssBaseline: {
            styleOverrides: (theme) => hfCssBaselineStyleOverrides(theme)
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    fontSize: px(HF_FONT_SIZES.small)
                }
            }
        },
        MuiSelect: {
            styleOverrides: {
                select: {
                    fontSize: px(HF_FONT_SIZES.small)
                }
            }
        },

        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontSize: px(HF_FONT_SIZES.small)
                }
            }
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    fontSize: px(HF_FONT_SIZES.small)
                }
            }
        },
        MuiFormControlLabel: {
            styleOverrides: {
                label: {
                    fontSize: px(HF_FONT_SIZES.small),
                    fontWeight: HF_FONT_WEIGHTS.bold
                }
            }
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiInputBase-input': {
                        fontSize: px(HF_FONT_SIZES.small)
                    },
                    '& .MuiInputLabel-root': {
                        fontSize: px(HF_FONT_SIZES.small)
                    }
                }
            }
        },
        MuiButtonBase: {
            styleOverrides: {
                root: {
                    textTransform: 'inherit',
                    borderRadius: 3,
                    '&.Mui-disabled': {
                        backgroundColor: 'transparent'
                    }
                }
            },
            defaultProps: {
                disableRipple: true,
                focusRipple: false,
                disableTouchRipple: true
            }
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'inherit',
                    border: `${HF_BORDER_WIDTHS.thin}px solid transparent`,
                    boxShadow: 'none',
                    borderRadius: HF_RADIUS.md,
                    fontFamily: defaultFontFamily,
                    fontWeight: HF_FONT_WEIGHTS.bold,
                    fontSize: px(HF_FONT_SIZES.small),
                    lineHeight: px(HF_LINE_HEIGHTS.bodyTight),
                    padding: `${HF_SPACING.xs + 2}px ${HF_SPACING.md}px`,
                    '&:hover': { boxShadow: 'none' },
                    '&:active': { boxShadow: 'none' },
                    '&.Mui-disabled': {
                        backgroundColor: 'transparent',
                        borderColor: 'transparent'
                    },
                    // Icon-only buttons (startIcon/endIcon with no label) — center the icon.
                    '&:has(> .MuiButton-startIcon:first-child:last-child), &:has(> .MuiButton-endIcon:first-child:last-child)': {
                        justifyContent: 'center',
                        minWidth: 'unset',
                        '& .MuiButton-startIcon, & .MuiButton-endIcon': {
                            margin: 0
                        }
                    }
                },
                sizeSmall: {
                    fontSize: px(HF_FONT_SIZES.small),
                    padding: `${HF_SPACING.xs}px ${HF_SPACING.sm}px`,
                    lineHeight: px(HF_LINE_HEIGHTS.small)
                },
                sizeLarge: {
                    fontSize: px(HF_FONT_SIZES.body),
                    padding: `${HF_SPACING.sm}px ${HF_SPACING.lg}px`,
                    lineHeight: px(HF_LINE_HEIGHTS.bodyTight)
                },
                containedPrimary: {
                    backgroundColor: HF_CTA_COLORS[500],
                    color: HF_BRAND_COLORS.white,
                    borderColor: HF_CTA_COLORS[500],
                    '&:hover': {
                        backgroundColor: HF_CTA_COLORS[600],
                        borderColor: HF_CTA_COLORS[600]
                    },
                    '&:active': {
                        backgroundColor: HF_CTA_COLORS[700],
                        borderColor: HF_CTA_COLORS[700]
                    },
                    '&.Mui-disabled': {
                        backgroundColor: HF_CTA_COLORS[300],
                        borderColor: HF_CTA_COLORS[300],
                        color: HF_BRAND_COLORS.white
                    }
                },
                containedError: {
                    backgroundColor: HF_SEMANTIC_COLORS.error[500],
                    color: HF_BRAND_COLORS.white,
                    borderColor: HF_SEMANTIC_COLORS.error[500],
                    '&:hover': {
                        backgroundColor: HF_SEMANTIC_COLORS.error[700],
                        borderColor: HF_SEMANTIC_COLORS.error[700]
                    }
                },
                containedSuccess: {
                    backgroundColor: HF_SEMANTIC_COLORS.success[500],
                    color: HF_BRAND_COLORS.white,
                    borderColor: HF_SEMANTIC_COLORS.success[500],
                    '&:hover': {
                        backgroundColor: HF_SEMANTIC_COLORS.success[600],
                        borderColor: HF_SEMANTIC_COLORS.success[600]
                    }
                },
                containedWarning: {
                    backgroundColor: HF_SEMANTIC_COLORS.warning[500],
                    color: HF_BRAND_COLORS.white,
                    borderColor: HF_SEMANTIC_COLORS.warning[500],
                    '&:hover': {
                        backgroundColor: HF_SEMANTIC_COLORS.warning[600],
                        borderColor: HF_SEMANTIC_COLORS.warning[600]
                    }
                },
                outlinedPrimary: {
                    color: HF_CTA_COLORS[500],
                    borderColor: HF_CTA_COLORS[500],
                    backgroundColor: 'transparent',
                    '&:hover': {
                        backgroundColor: HF_CTA_COLORS[100],
                        borderColor: HF_CTA_COLORS[600],
                        color: HF_CTA_COLORS[600]
                    },
                    '&:active': {
                        backgroundColor: HF_CTA_COLORS[200],
                        borderColor: HF_CTA_COLORS[700],
                        color: HF_CTA_COLORS[700]
                    },
                    '&.Mui-disabled': {
                        color: HF_CTA_COLORS[300],
                        borderColor: HF_CTA_COLORS[300]
                    }
                },
                outlinedError: {
                    color: HF_SEMANTIC_COLORS.error[500],
                    borderColor: HF_SEMANTIC_COLORS.error[500],
                    '&:hover': {
                        backgroundColor: HF_SEMANTIC_COLORS.error[100],
                        borderColor: HF_SEMANTIC_COLORS.error[700],
                        color: HF_SEMANTIC_COLORS.error[700]
                    }
                },
                outlinedSuccess: {
                    color: HF_SEMANTIC_COLORS.success[500],
                    borderColor: HF_SEMANTIC_COLORS.success[500],
                    '&:hover': {
                        backgroundColor: HF_SEMANTIC_COLORS.success[100],
                        borderColor: HF_SEMANTIC_COLORS.success[600],
                        color: HF_SEMANTIC_COLORS.success[600]
                    }
                },
                text: {
                    border: `${HF_BORDER_WIDTHS.thin}px solid transparent`,
                    '&:hover': {
                        backgroundColor: HF_CTA_COLORS[100]
                    }
                },
                textPrimary: {
                    color: HF_CTA_COLORS[500],
                    '&:hover': {
                        color: HF_CTA_COLORS[600],
                        backgroundColor: HF_CTA_COLORS[100]
                    }
                }
            },
            defaultProps: {
                disableRipple: true,
                focusRipple: false,
                disableTouchRipple: true,
                disableElevation: true
            }
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    fontSize: px(HF_FONT_SIZES.mini)
                }
            }
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    fontFamily: defaultFontFamily,
                    fontSize: px(HF_FONT_SIZES.mini),
                    fontStyle: 'normal',
                    fontWeight: HF_FONT_WEIGHTS.regular,
                    lineHeight: px(HF_LINE_HEIGHTS.mini),
                    letterSpacing: '0em',
                    color: defaultTextColor,
                    transform: 'none'
                }
            }
        },
        MuiSvgIcon: {
            styleOverrides: {
                fontSizeSmall: {
                    fontSize: px(HF_ICON_SIZES.sm)
                },
                fontSizeMedium: {
                    fontSize: px(HF_ICON_SIZES.md)
                },
                fontSizeLarge: {
                    fontSize: px(HF_ICON_SIZES.xxl)
                }
            }
        },
        MuiChip: {
            styleOverrides : {
                root: {
                    fontFamily: defaultFontFamily,
                    fontSize: px(HF_FONT_SIZES.mini),
                    fontStyle: 'normal',
                    fontWeight: HF_FONT_WEIGHTS.regular,
                    lineHeight: px(HF_LINE_HEIGHTS.small),
                    letterSpacing: '0em',
                    borderRadius: HF_RADIUS.pill,
                    height: 'auto',
                    paddingTop: HF_SPACING.xs - 1,
                    paddingBottom: HF_SPACING.xs - 1,
                    paddingLeft: HF_SPACING.sm,
                    paddingRight: HF_SPACING.sm,
                    '& .MuiChip-label': {
                        paddingLeft: HF_SPACING.xs,
                        paddingRight: HF_SPACING.xs
                    }
                },
                sizeSmall: {
                    fontSize: px(HF_FONT_SIZES.mini),
                    height: 'auto'
                },
                colorPrimary: {
                    backgroundColor: HF_CTA_COLORS[100],
                    color: HF_CTA_COLORS[600],
                    '& .MuiChip-deleteIcon': { color: HF_CTA_COLORS[600] }
                },
                colorSuccess: {
                    backgroundColor: HF_SEMANTIC_COLORS.success[100],
                    color: HF_SEMANTIC_COLORS.success[600],
                    '& .MuiChip-deleteIcon': { color: HF_SEMANTIC_COLORS.success[600] }
                },
                colorWarning: {
                    backgroundColor: HF_SEMANTIC_COLORS.warning[100],
                    color: HF_SEMANTIC_COLORS.warning[600],
                    '& .MuiChip-deleteIcon': { color: HF_SEMANTIC_COLORS.warning[600] }
                },
                colorError: {
                    backgroundColor: HF_SEMANTIC_COLORS.error[200],
                    color: HF_SEMANTIC_COLORS.error[700],
                    '& .MuiChip-deleteIcon': { color: HF_SEMANTIC_COLORS.error[700] }
                },
                colorDefault: {
                    backgroundColor: HF_NEUTRAL_COLORS[200],
                    color: HF_NEUTRAL_COLORS[600],
                    '& .MuiChip-deleteIcon': { color: HF_NEUTRAL_COLORS[500] }
                },
                outlined: {
                    backgroundColor: 'transparent'
                },
                outlinedPrimary: {
                    borderColor: HF_CTA_COLORS[500],
                    color: HF_CTA_COLORS[600]
                }
            },
            variants: [
                {
                    props: { variant: 'outlined' as const, color: 'success' as const },
                    style: {
                        borderColor: HF_SEMANTIC_COLORS.success[500],
                        color: HF_SEMANTIC_COLORS.success[600]
                    }
                },
                {
                    props: { variant: 'outlined' as const, color: 'warning' as const },
                    style: {
                        borderColor: HF_SEMANTIC_COLORS.warning[500],
                        color: HF_SEMANTIC_COLORS.warning[600]
                    }
                },
                {
                    props: { variant: 'outlined' as const, color: 'error' as const },
                    style: {
                        borderColor: HF_SEMANTIC_COLORS.error[500],
                        color: HF_SEMANTIC_COLORS.error[700]
                    }
                }
            ]
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontFamily: defaultFontFamily,
                    fontSize: px(HF_FONT_SIZES.mini),
                    fontStyle: 'normal',
                    lineHeight: px(HF_LINE_HEIGHTS.mini),
                    color: defaultTextColor
                }
            }
        },
        MuiLink: {
            styleOverrides: {
                root: ({ theme }) => ({
                    fontSize: px(HF_FONT_SIZES.small),
                    color: theme.palette.primary.main,
                    textDecoration: 'none',
                    cursor: 'pointer',
                    '&:hover': {
                        color: theme.palette.primary.dark,
                        textDecoration: 'underline',
                        cursor: 'pointer'
                    }
                })
            }
        },
        MuiTreeItem: {
            styleOverrides: {
                root: {
                    fontSize: px(HF_FONT_SIZES.small),
                    '& > .MuiTreeItem-content': {
                        fontSize: px(HF_FONT_SIZES.small),
                        '& > .MuiTreeItem-label': {
                            fontSize: px(HF_FONT_SIZES.small)
                        }
                    }
                }
            }
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    fontSize: px(HF_FONT_SIZES.small),
                    padding: '0px'
                },
                input: {
                    paddingTop: '8px !important',
                    paddingBottom: '8px !important'
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: HF_RADIUS.md,
                    backgroundColor: HF_BRAND_COLORS.white,
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: HF_NEUTRAL_COLORS[300],
                        borderWidth: HF_BORDER_WIDTHS.thin
                    },
                    '&:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
                        borderColor: HF_NEUTRAL_COLORS[400]
                    },
                    '&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
                        borderColor: HF_CTA_COLORS[500],
                        borderWidth: HF_BORDER_WIDTHS.medium
                    },
                    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                        borderColor: HF_SEMANTIC_COLORS.error[500],
                        borderWidth: HF_BORDER_WIDTHS.medium
                    },
                    '&.Mui-disabled': {
                        backgroundColor: HF_NEUTRAL_COLORS[100],
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: HF_NEUTRAL_COLORS[200]
                        }
                    },
                    '& .MuiInputAdornment-positionEnd .MuiIconButton-root': {
                        padding: `${HF_SPACING.sm}px`,
                        '& .MuiSvgIcon-root': {
                            fontSize: HF_LAYOUT_SIZES.datePickerIconPx
                        }
                    }
                }
            }
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none'
                },
                outlined: {
                    border: HF_BORDERS.divider,
                    borderRadius: HF_RADIUS.md
                }
            },
            variants: [
                {
                    props: { variant: 'caja' as const },
                    style: {
                        backgroundColor: HF_BRAND_COLORS.white,
                        borderRadius: HF_RADIUS.md,
                        boxShadow: HF_SHADOWS.card,
                        border: 'none',
                        padding: HF_SPACING.xl
                    }
                }
            ]
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    backgroundColor: HF_BRAND_COLORS.white,
                    borderRadius: HF_RADIUS.md,
                    boxShadow: HF_SHADOWS.card,
                    border: 'none',
                    backgroundImage: 'none'
                }
            }
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    padding: HF_SPACING.xl,
                    '&:last-child': { paddingBottom: HF_SPACING.xl }
                }
            }
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: HF_RADIUS.md,
                    boxShadow: HF_SHADOWS.pop,
                    backgroundImage: 'none'
                }
            }
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    fontFamily: defaultFontFamily,
                    fontSize: px(HF_FONT_SIZES.h2),
                    fontWeight: HF_FONT_WEIGHTS.bold,
                    lineHeight: px(HF_LINE_HEIGHTS.h2),
                    color: HF_NEUTRAL_COLORS[700],
                    padding: `${HF_SPACING.lg}px ${HF_SPACING.xl}px`,
                    borderBottom: HF_BORDERS.divider
                }
            }
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    padding: `${HF_SPACING.xl}px`
                }
            }
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    padding: `${HF_SPACING.md}px ${HF_SPACING.xl}px`,
                    borderTop: HF_BORDERS.divider,
                    gap: HF_SPACING.sm
                }
            }
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: HF_NEUTRAL_COLORS[200]
                }
            }
        },
        MuiMenu : {
            styleOverrides: {
                root: {
                    zIndex: HF_Z_INDEX.overlay
                }
            }
        },
        MuiPopper: {
            styleOverrides: {
                root: {
                    zIndex: HF_Z_INDEX.overlay
                }
            },
            defaultProps: {
                sx: {
                    '&.MuiPickersPopper-root': {
                        zIndex: HF_Z_INDEX.overlay
                    }
                }
            }
        },
        MuiPopover: {
            styleOverrides: {
                root: {
                    zIndex: HF_Z_INDEX.overlay
                }
            }
        },
        MuiAutocomplete: {
            styleOverrides: {
                // Keep outlined Autocompletes (incl. multi-select with chips) at the
                // same 36px height as TextField / Select instead of MUI's 9px padding.
                inputRoot: {
                    '&.MuiOutlinedInput-root:not(.MuiInputBase-sizeSmall)': {
                        paddingTop: 1,
                        paddingBottom: 1,
                        paddingLeft: HF_SPACING.sm,
                        '& .MuiAutocomplete-input': {
                            paddingTop: '7px !important',
                            paddingBottom: '7px !important'
                        }
                    }
                }
            }
        },
        MuiPagination: {
            defaultProps: {
                size: 'small',
                siblingCount: 1,
                boundaryCount: 1,
                color: 'primary',
                showFirstButton: false,
                showLastButton: false
            },
            styleOverrides: {
                ul: {
                    flexWrap: 'nowrap',
                    gap: HF_SPACING.xs
                }
            }
        },
        MuiPaginationItem: {
            styleOverrides: {
                root: {
                    fontFamily: defaultFontFamily,
                    fontSize: px(HF_FONT_SIZES.small),
                    fontWeight: HF_FONT_WEIGHTS.regular,
                    borderRadius: HF_RADIUS.md
                },
                sizeSmall: {
                    minWidth: 28,
                    height: 28,
                    margin: 0
                }
            }
        },
        MuiTablePagination: {
            defaultProps: {
                size: 'small'
            },
            styleOverrides: {
                root: {
                    fontFamily: defaultFontFamily,
                    fontSize: px(HF_FONT_SIZES.small)
                },
                selectLabel: {
                    fontSize: px(HF_FONT_SIZES.small)
                },
                displayedRows: {
                    fontSize: px(HF_FONT_SIZES.small)
                }
            }
        }
    }
});
